<script lang="ts">
	import type { SearchField } from 'utility/search-fields'
	import type { Snippet } from 'svelte'
	import type { SvelteSet } from 'svelte/reactivity'

	import { slide } from 'svelte/transition'
	import Button from '@isoftdata/svelte-button'

	interface Props {
		searchField: SearchField
		inEditMode: boolean
		pinnedSearchFields: SvelteSet<SearchField['key']> //Required
		selectedSearchFieldsKeys: SvelteSet<SearchField['key']> //Required
		children?: Snippet
	}

	let {
		searchField,
		inEditMode,
		pinnedSearchFields,
		selectedSearchFieldsKeys = $bindable(),
		children,
	}: Props = $props()

	let isDragOver: boolean = $state(false)

	function pinField() {
		if (pinnedSearchFields.has(searchField.key)) {
			pinnedSearchFields.delete(searchField.key)
			if (searchField.dependants?.length) {
				for (const dependant of searchField.dependants) {
					pinnedSearchFields.delete(dependant)
				}
			}
		} else {
			pinnedSearchFields.add(searchField.key)
			//Also pin any dependencies the search field might have
			for (const dependency of searchField.dependencies ?? []) {
				pinnedSearchFields.add(dependency)
			}
		}
	}

	function removeSearchField() {
		selectedSearchFieldsKeys.delete(searchField.key)
		if (searchField.dependants?.length) {
			for (const dependant of searchField.dependants) {
				selectedSearchFieldsKeys.delete(dependant)
			}
		}
		selectedSearchFieldsKeys = selectedSearchFieldsKeys
	}
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
	class="col-12 col-md-6 col-lg-4 col-xl-3 parent-col"
	class:is-drag-over={isDragOver}
	class:in-edit-mode={inEditMode}
	draggable={inEditMode}
	ondragstart={event => {
		if (event.dataTransfer) {
			event.dataTransfer.setData('application/search-field-key', searchField.key)
			event.dataTransfer.setDragImage(event.currentTarget, 0, 0)
			event.dataTransfer.dropEffect = 'move'
		}
	}}
	ondragover={event => {
		event.preventDefault()
		if (event.dataTransfer) {
			event.dataTransfer.dropEffect = 'move'
		}
		isDragOver = inEditMode
	}}
	ondragleave={() => {
		isDragOver = false
	}}
	ondrop={event => {
		event.preventDefault()
		if (event.dataTransfer) {
			const drugKey = event.dataTransfer.getData('application/search-field-key')
			if (!drugKey || drugKey === searchField.key) {
				return
			}

			console.log('Dropped key:', drugKey, 'onto', searchField.key)

			const selectedFieldsArray = Array.from(selectedSearchFieldsKeys)
			const drugIndex = selectedFieldsArray.indexOf(drugKey)
			const hoveredIndex = selectedFieldsArray.indexOf(searchField.key)

			const newDrugIndex = hoveredIndex < drugIndex ? hoveredIndex : hoveredIndex + 1

			selectedFieldsArray.splice(drugIndex, 1)
			selectedFieldsArray.splice(newDrugIndex, 0, drugKey)

			// Clear and re-add keys to selectedSearchFieldsKeys in new order
			selectedSearchFieldsKeys.clear()
			for (const key of selectedFieldsArray) {
				selectedSearchFieldsKeys.add(key)
			}
			isDragOver = false
		}
	}}
>
	<div class="form-row align-items-end">
		<div class="col">
			{@render children?.()}
			<!-- Search Field goes here -->
		</div>
		{#if inEditMode}
			<div
				class="col-auto mb-1 d-flex flex-nowrap"
				transition:slide={{ duration: 200, axis: 'x' }}
			>
				<Button
					iconClass="thumbtack"
					class="mr-1"
					size="sm"
					outline={!pinnedSearchFields.has(searchField.key)}
					onclick={pinField}
					title={pinnedSearchFields.has(searchField.key) ? 'Unpin this search field' : 'Pin this search field'}
				/>
				{#if !searchField.alwaysVisible}
					<Button
						iconClass="trash"
						size="sm"
						outline
						color="danger"
						onclick={removeSearchField}
						title="Remove this search field"
					/>
				{/if}
			</div>
		{/if}
	</div>
</div>

<!-- We want to display a border when you mouse over a search fields. In order to not have the UI shift during that, we need to adjust the padding based on the hover/edit mode state -->
<style>
	.parent-col:not(:hover),
	.parent-col:hover:not(.in-edit-mode) {
		padding-top: 1px;
		padding-bottom: 1px;
	}

	.parent-col.in-edit-mode:hover,
	.is-drag-over {
		border: 1px solid #dee2e6 !important;
		border-color: #007bff !important;
		border-radius: 0.25rem !important;
		padding-right: 4px !important;
		padding-left: 4px !important;
	}
</style>
